import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Col, Collapse, Row } from 'antd'
import { Formik, FormikProps } from 'formik'
import moment from 'moment'
import * as Yup from 'yup'

import statesApi, { StateValues } from 'core/application/commons/states'
import { ApplicationInfo, BudgetGoal, Plan } from 'core/application/ichraDecisionTool/prospectSignup/state'
import question6Api, { Question6Values } from 'core/application/ichraDecisionTool/prospectSignup/step2/question6'

import Tooltip from 'common/components/dataDisplay/Tooltip'
import Checkbox from 'common/components/dataEntry/components/Checkbox'
import Option from 'common/components/dataEntry/components/Option'
import FormikCheckboxGroupField from 'common/components/dataEntry/formik/FormikCheckboxGroupField'
import Input from 'common/components/dataEntry/formik/FormikDefaultInputField'
import Select from 'common/components/dataEntry/formik/FormikDefaultSelectField'
import Radio from 'common/components/dataEntry/formik/FormikRadioField'
import Alert from 'common/components/feedback/Alert'
import notification from 'common/components/feedback/Notification'
import Button from 'common/components/general/Button'
import { filterByValue } from 'common/components/utils/SelectFilterUtils'

import { commandRequest } from 'common/RequestUtils'
import { formatMoney } from 'common/moneyFormatter'
import { BaseNavigationProps } from 'common/wizard/BaseNavigationProps'

import CategoryItemPageHeader from '../common/CategoryItemPageHeader'

enum EmployeeContributionOptions {
  employeeOnly = 0,
  employeeAndFamily = 1,
}

const formValidator = (translate: any) =>
  Yup.object({
    budgetGoal: Yup.number().required(translate('required')),
    decreaseCurrentSpendPercentage: Yup.number()
      .nullable()
      .when('budgetGoal', {
        is: (budgetGoal: number) => budgetGoal == 1,
        then: Yup.number().nullable().required(translate('required')),
      }),
    employeeContributionStrategy: Yup.number().required(translate('required')),
    reimbursementOptions: Yup.number().required(translate('required')),
    offerDifferentAmount: Yup.boolean().required(translate('required')),
    offerTraditionalGroupHealthInsurance: Yup.boolean().required(translate('required')),
    employeeCategories: Yup.array()
      .nullable()
      .when('offerDifferentAmount', {
        is: (offerDifferentAmount: boolean) => offerDifferentAmount,
        then: Yup.array().min(1, translate('at-least-one')).required(translate('required')).nullable(),
      }),
  })

const initialValues: Question6Values = {
  budgetGoal: undefined,
  decreaseCurrentSpendPercentage: undefined,
  employeeCategories: [],
  employeeContributionStrategy: undefined,
  offerDifferentAmount: undefined,
  offerTraditionalGroupHealthInsurance: undefined,
  reimbursementOptions: undefined,
  id: '',
}

export type Step2Question6Props = Pick<
  Plan,
  | 'budgetGoal'
  | 'organizationSpendPerMonth'
  | 'budgetGoalsOptions'
  | 'employeeContributionStrategyOptions'
  | 'availableReimbursementOptions'
  | 'employeeCategoryOptions'
  | 'id'
> &
  Pick<ApplicationInfo, 'currentPlanStatus'>

const Question6: React.FC<BaseNavigationProps<Step2Question6Props>> = (props) => {
  const { t: translate } = useTranslation('ichra-decision-tool')
  const [loading, setLoading] = useState<boolean>(false)
  const [statesList, setStatesList] = useState<StateValues[]>()

  useEffect(() => {
    statesApi().then((response) => {
      setStatesList(response)
    })
  }, [])

  const onSubmit = async (values: Question6Values) => {
    setLoading(true)

    const decreaseCurrentSpendPercentage = values.decreaseCurrentSpendPercentage
      ? +values.decreaseCurrentSpendPercentage
      : undefined

    const apiValues: Question6Values = {
      budgetGoal: values.budgetGoal,
      decreaseCurrentSpendPercentage: values.budgetGoal == 1 ? decreaseCurrentSpendPercentage : undefined,
      employeeContributionStrategy: values.employeeContributionStrategy,
      reimbursementOptions: values.reimbursementOptions,
      offerDifferentAmount: values.offerDifferentAmount,
      offerTraditionalGroupHealthInsurance: values.offerTraditionalGroupHealthInsurance,
      employeeCategories: values.offerDifferentAmount ? values.employeeCategories : [],
      id: props.state.id,
    }

    const res = await commandRequest(() => question6Api(apiValues))
    setLoading(false)
    if (!res.errors) {
      props.goNext(props.name)
    } else {
      notification.open({
        message: res.errors.join(','),
        type: 'error',
      })
    }
  }

  const getNextButton = (formik: FormikProps<Question6Values>): ReactNode => {
    const nextButton = (
      <Button
        icon="fa fa-arrow-right"
        iconPosition="right"
        size="large"
        htmlType="submit"
        type="primary"
        disabled={!formValidator(translate).isValidSync(formik.values)}
        className="float-right next__button"
        loading={loading}
      >
        {translate('next')}
      </Button>
    )

    const missingInput = !formValidator(translate).isValidSync(formik.values)

    return missingInput ? (
      <Tooltip title={translate('next-tooltip')} position="left">
        {nextButton}
      </Tooltip>
    ) : (
      nextButton
    )
  }

  const getDecreaseCurrentSpendPercentageMessage = useCallback(
    (decreasePercentage: number) => {
      return (
        props.state.organizationSpendPerMonth &&
        `${decreasePercentage}% X ${formatMoney(props.state.organizationSpendPerMonth)} = ${formatMoney(
          getDecreasedValue(decreasePercentage),
        )} `
      )
    },
    [props.state.organizationSpendPerMonth],
  )

  const getDesiredValue = useCallback(
    (decreasePercentage: number) => {
      return `${translate('prospect-signup.step-2.question-6.desired-spend')} ${getSpendValue(decreasePercentage)}`
    },
    [props.state.organizationSpendPerMonth],
  )

  const getDecreasedValue = useCallback(
    (decreasePercentage: number) => {
      return (decreasePercentage / 100) * props.state.organizationSpendPerMonth
    },
    [props.state.organizationSpendPerMonth],
  )

  const getSpendValue = useCallback(
    (decreasePercentage: number) => {
      return formatMoney(props.state.organizationSpendPerMonth - getDecreasedValue(decreasePercentage))
    },
    [props.state.organizationSpendPerMonth],
  )

  return (
    <Formik
      enableReinitialize={true}
      initialValues={props.state.budgetGoal ?? initialValues}
      validationSchema={formValidator(translate)}
      onSubmit={onSubmit}
      validate={(values) => {
        props.hasDirtyData(true)
      }}
    >
      {(formik) => (
        <form onSubmit={formik.handleSubmit} autoComplete="off" onBlur={formik.handleBlur}>
          <Row>
            <Col xs={24} sm={24} md={13}>
              <CategoryItemPageHeader
                title={translate('prospect-signup.step-2.question-1-title')}
                numberOfQuestions={6}
                currentQuestion={6}
                question={translate('prospect-signup.step-2.question-6.question')}
                className="ml-0"
                titleFontSize={20}
              />
            </Col>
          </Row>

          <Row>
            <Col xs={24} sm={24} md={13}>
              <Row>
                <Col xs={24} sm={24} md={24}>
                  <Row>
                    <Radio.Group name="budgetGoal" defaultValue={props.state.budgetGoal?.budgetGoal}>
                      <Row>
                        {props.state.budgetGoalsOptions.map((option, index) => (
                          <Col className="flex__space_between">
                            <div className="full-width">
                              <Radio.Button value={option.id}>
                                {option.name}{' '}
                                {index == 0 &&
                                  props.state.organizationSpendPerMonth &&
                                  formatMoney(props.state.organizationSpendPerMonth)}
                              </Radio.Button>
                            </div>
                          </Col>
                        ))}
                      </Row>
                    </Radio.Group>
                  </Row>

                  {formik.values.budgetGoal == 1 && (
                    <Row>
                      <Col className="mr-8 mt-16">
                        <Input
                          type="number"
                          name="decreaseCurrentSpendPercentage"
                          label={translate('prospect-signup.step-2.question-6.percentage-decrease')}
                          prefix={'%'}
                          min={1}
                        />
                      </Col>
                    </Row>
                  )}

                  {formik.values.budgetGoal == 1 &&
                    formik.values.decreaseCurrentSpendPercentage &&
                    formik.values.decreaseCurrentSpendPercentage > 0 && (
                      <Row>
                        <Col className="mr-8">
                          <Alert
                            type="info"
                            message={
                              <div>
                                <span>
                                  {getDecreaseCurrentSpendPercentageMessage(
                                    formik.values.decreaseCurrentSpendPercentage,
                                  )}
                                </span>
                                <br />
                                <span>{getDesiredValue(formik.values.decreaseCurrentSpendPercentage)}</span>
                              </div>
                            }
                            description={' '}
                            showIcon
                          />
                        </Col>
                      </Row>
                    )}

                  <Row>
                    <Col className="mt-32">
                      <Radio.Group
                        name="employeeContributionStrategy"
                        defaultValue={props.state.budgetGoal?.employeeContributionStrategy}
                        label={translate('prospect-signup.step-2.question-6.contribution-strategy')}
                      >
                        <Row>
                          <Col className="flex__space_between">
                            <div className="full-width">
                              <Radio.Button value={EmployeeContributionOptions.employeeAndFamily}>
                                {translate('yes')}
                              </Radio.Button>
                            </div>
                          </Col>
                          <Col className="flex__space_between">
                            <div className="full-width">
                              <Radio.Button value={EmployeeContributionOptions.employeeOnly}>
                                {translate('no')}
                              </Radio.Button>
                            </div>
                          </Col>
                        </Row>
                      </Radio.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col className="mt-16">
                      <Radio.Group
                        name="reimbursementOptions"
                        defaultValue={props.state.budgetGoal?.reimbursementOptions}
                        label={translate('prospect-signup.step-2.question-6.eligible-expenses')}
                      >
                        <Row>
                          {props.state.availableReimbursementOptions.map((option) => (
                            <Col className="flex__space_between">
                              <div className="full-width">
                                <Radio.Button value={option.id}>{option.name}</Radio.Button>
                              </div>
                            </Col>
                          ))}
                        </Row>
                      </Radio.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col className="mt-24">
                      <Radio.Group
                        name="offerTraditionalGroupHealthInsurance"
                        defaultValue={props.state.budgetGoal?.offerTraditionalGroupHealthInsurance}
                        label={translate('prospect-signup.step-2.question-6.traditional-insurance')}
                      >
                        <Row>
                          <Col className="flex__space_between" md={12}>
                            <div className="full-width">
                              <Radio.Button value={true}>{translate('yes')}</Radio.Button>
                            </div>
                          </Col>
                          <Col className="flex__space_between" md={12}>
                            <div className="full-width">
                              <Radio.Button value={false}>{translate('no')}</Radio.Button>
                            </div>
                          </Col>
                        </Row>
                      </Radio.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col className="mt-24">
                      <p className="mb-0 pb-0">{translate('prospect-signup.step-2.question-6.employee-category')}</p>
                      <ul className="pl-16 mt-0">
                        <li>{translate('prospect-signup.step-2.question-6.employee-category-option-1')}</li>
                        <li>{translate('prospect-signup.step-2.question-6.employee-category-option-2')}</li>
                      </ul>
                      <Radio.Group
                        name="offerDifferentAmount"
                        defaultValue={props.state.budgetGoal?.offerDifferentAmount}
                      >
                        <Row>
                          <Col className="flex__space_between" md={12}>
                            <div className="full-width">
                              <Radio.Button value={true}>{translate('yes')}</Radio.Button>
                            </div>
                          </Col>
                          <Col className="flex__space_between" md={12}>
                            <div className="full-width">
                              <Radio.Button value={false}>{translate('no')}</Radio.Button>
                            </div>
                          </Col>
                        </Row>
                      </Radio.Group>
                    </Col>
                  </Row>
                  {formik.values.offerDifferentAmount && (
                    <>
                      <Row className="form__row">
                        <Col xs={24} sm={24} md={24}>
                          <FormikCheckboxGroupField
                            defaultValue={props.state.budgetGoal?.employeeCategories}
                            name="employeeCategories"
                            label={translate('prospect-signup.step-2.question-6.select-all-that-apply')}
                          >
                            {props.state.employeeCategoryOptions.map((option) => (
                              <Col md={12} className="pr-8">
                                <Checkbox value={option.id} key={option.id} white className="mb-8 ml-0">
                                  {option.name}
                                </Checkbox>
                              </Col>
                            ))}
                          </FormikCheckboxGroupField>
                        </Col>
                      </Row>
                    </>
                  )}
                </Col>
              </Row>
            </Col>
          </Row>

          <Row>
            <Col xs={24}>
              <div className="bottom__row">
                <Button
                  icon="fa fa-arrow-left"
                  size="large"
                  htmlType="button"
                  type="default"
                  onClick={() => {
                    props.goBack(props.name)
                  }}
                >
                  {translate('back')}
                </Button>

                {getNextButton(formik)}
              </div>
            </Col>
          </Row>
        </form>
      )}
    </Formik>
  )
}

export default Question6
